import type {
  AccountCarrierMode,
  CarrierConnectionDetailsDto,
  CarrierConnectionDto,
  CarrierProvisioningConfiguration,
} from '@fv/models'

import type { CarrierConnectionFormModel } from './types'

export const MIN_CARRIER_SEARCH_LENGTH = 3

export const canShowProvisioningUI = (
  carrier: Pick<
    CarrierConnectionDto,
    'supportsApi' | 'isApiIntegrated' | 'enableLTL'
  >,
  restrictNewLtlCarriers: boolean,
) =>
  carrier.supportsApi &&
  (!restrictNewLtlCarriers || carrier.isApiIntegrated || !carrier.enableLTL)

export const filterCarrierSearchResults = (
  searchResults: CarrierProvisioningConfiguration[],
  networkCarriers: CarrierConnectionDto[],
  restrictNewLtlCarriers: boolean,
) => {
  const networkById = new Map(networkCarriers.map(c => [c.carrierId, c]))
  return searchResults.filter(carrier => {
    const connection = networkById.get(carrier.carrierId)
    return (
      !connection ||
      !connection.hasSpotContacts ||
      (!connection.isApiIntegrated &&
        canShowProvisioningUI(connection, restrictNewLtlCarriers))
    )
  })
}

export const getCarrierModeDisplay = (
  override: string | undefined,
  mode: AccountCarrierMode,
) => {
  if (override) return override

  if (mode === 'tl') return 'Truckload'
  if (mode === 'parcel') return 'Parcel'
  return 'LTL'
}

export const toFormModel = (
  val: CarrierConnectionDetailsDto | undefined,
): CarrierConnectionFormModel => ({
  carrierId: val?.carrierId ?? '',
  credentials: val?.credentials ?? [],
  salesRep: {
    firstName: val?.salesRep?.firstName ?? '',
    lastName: val?.salesRep?.lastName ?? '',
    phone: val?.salesRep?.phone ?? '',
  },
  salesRepEmails: val?.salesRep?.emails?.join() ?? '',
  enableLTL: val?.enableLTL ?? true,
  enableTL: val?.enableTL ?? true,
  enableParcel: val?.enableParcel ?? true,
})

export const fillModeDefaults = (
  model: CarrierConnectionFormModel,
  carrier: CarrierProvisioningConfiguration,
): CarrierConnectionFormModel => ({
  ...model,
  enableLTL: model.enableLTL ?? carrier.ltl.enabled,
  enableParcel: model.enableParcel ?? carrier.parcel.enabled,
  enableTL: model.enableTL ?? carrier.tl.enabled,
})

export const isLtlModeRestricted = (args: {
  restrictNewLtlCarriers: boolean
  hadLtlEnabled: boolean
}) => {
  return args.restrictNewLtlCarriers && !args.hadLtlEnabled
}
